import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { MarketingDashboard } from "./MarketingDashboard";
import { MarketingBudget } from "./MarketingBudget";
import {
  Plus, MagnifyingGlass, CaretLeft, CaretRight, ChartPie, Megaphone,
  User, Phone, Envelope, Buildings, Tag, ChatCircleDots, Trash, Check,
  FunnelSimple, Gear, PencilSimple, Wallet,
} from "@phosphor-icons/react";

function StatusBadge({ status, statuses }) {
  const s = (statuses || []).find(st => st.name === status);
  return (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border" style={{ backgroundColor: s?.color + "18", color: s?.color, borderColor: s?.color + "40" }}>
      {s?.label || status}
    </span>
  );
}

function CampaignBadge({ type, campaigns }) {
  const c = (campaigns || []).find(ct => ct.name === type);
  return (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: c?.color + "18", color: c?.color }}>
      {c?.label || type}
    </span>
  );
}

export default function MarketingModule() {
  const { token, user } = useAuth();
  const [view, setView] = useState("list");
  const [leads, setLeads] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [page, setPage] = useState(1);
  const [statuses, setStatuses] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const isAdmin = user?.role === "admin";
  const headers = { Authorization: `Bearer ${token}` };

  const loadMeta = useCallback(async () => {
    const [st, ct, sl] = await Promise.all([
      axios.get(`${API}/marketing/status-types`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/marketing/campaign-types`, { headers }).catch(() => ({ data: [] })),
      isAdmin ? axios.get(`${API}/marketing/sellers`, { headers }).catch(() => ({ data: [] })) : { data: [] },
    ]);
    setStatuses(st.data); setCampaigns(ct.data); setSellers(sl.data);
  }, [token]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    let url = `${API}/marketing/leads?page=${page}&limit=50`;
    if (filterStatus !== "all") url += `&status=${filterStatus}`;
    if (filterCampaign !== "all") url += `&campaign_type=${filterCampaign}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const { data } = await axios.get(url, { headers }).catch(() => ({ data: { items: [], total: 0, page: 1, pages: 1 } }));
    setLeads(data);
    setLoading(false);
  }, [token, page, filterStatus, filterCampaign, search]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  const openDetail = (lead) => { setSelectedLead(lead); setView("detail"); };

  if (view === "dashboard") return <MarketingDashboard onBack={() => setView("list")} />;
  if (view === "budget") return <MarketingBudget onBack={() => setView("list")} />;

  if (view === "detail" && selectedLead) return (
    <LeadDetail leadId={selectedLead.id} statuses={statuses} campaigns={campaigns} sellers={sellers}
      onBack={() => { setView("list"); loadLeads(); }} isAdmin={isAdmin} />
  );

  return (
    <div className="space-y-4" data-testid="marketing-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Marketing</h2>
          <p className="text-sm text-slate-500">Gestion de leads y campanas</p>
        </div>
        <div className="flex items-center gap-2">
          {(isAdmin || user?.role === "marketing") && (
            <>
              <Button variant="outline" size="sm" onClick={() => setView("dashboard")} data-testid="mkt-dashboard-btn">
                <ChartPie size={16} className="mr-1" /> Dashboard
              </Button>
              <Button variant="outline" size="sm" onClick={() => setView("budget")} data-testid="mkt-budget-btn">
                <Wallet size={16} className="mr-1" /> Presupuesto
              </Button>
            </>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="mkt-settings-btn">
              <Gear size={16} className="mr-1" /> Config
            </Button>
          )}
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="mkt-new-lead-btn">
            <Plus size={16} className="mr-1" /> Nuevo Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border p-3 shadow-sm">
        <FunnelSimple size={16} className="text-slate-400" />
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Buscar lead..." className="pl-8 h-8 text-xs" data-testid="mkt-search" />
        </div>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[150px] text-xs h-8" data-testid="mkt-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estatus</SelectItem>
            {statuses.map(s => <SelectItem key={s.name} value={s.name}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCampaign} onValueChange={(v) => { setFilterCampaign(v); setPage(1); }}>
          <SelectTrigger className="w-[150px] text-xs h-8" data-testid="mkt-filter-campaign"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las campanas</SelectItem>
            {campaigns.map(c => <SelectItem key={c.name} value={c.name}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-auto">{leads.total} leads</span>
      </div>

      {/* Table */}
      {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div> : leads.items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border">
          <Megaphone size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">Sin leads registrados</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="overflow-auto">
            <table className="w-full text-xs" data-testid="mkt-leads-table">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left py-2.5 px-3 font-semibold">Empresa</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Contacto</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Campana</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Estatus</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Asesor</th>
                  <th className="text-center py-2.5 px-3 font-semibold">Aprobado</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Fecha</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.items.map(l => (
                  <tr key={l.id} className="border-t hover:bg-slate-50/70 cursor-pointer" onClick={() => openDetail(l)} data-testid={`mkt-row-${l.id}`}>
                    <td className="py-2.5 px-3 text-slate-700 font-medium">{l.company_name || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-600">{l.contact_name}</td>
                    <td className="py-2.5 px-3"><CampaignBadge type={l.campaign_type} campaigns={campaigns} /></td>
                    <td className="py-2.5 px-3"><StatusBadge status={l.status} statuses={statuses} /></td>
                    <td className="py-2.5 px-3 text-slate-500">{l.assigned_to_name || "Sin asignar"}</td>
                    <td className="py-2.5 px-3 text-center">
                      {l.comercial_contact_id ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" title="Sincronizado a Comercial">
                          <Check size={10} /> Comercial
                        </span>
                      ) : l.approved_by_admin ? <Check size={14} className="text-emerald-500 mx-auto" /> : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-slate-400">{l.created_at?.slice(0, 10)}</td>
                    <td className="py-2.5 px-3"><Button variant="ghost" size="sm" className="text-xs h-6">Ver</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {leads.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
              <span className="text-xs text-slate-500">Pag {leads.page} de {leads.pages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded-lg hover:bg-white disabled:opacity-30"><CaretLeft size={16} /></button>
                <button onClick={() => setPage(p => Math.min(leads.pages, p + 1))} disabled={page >= leads.pages} className="p-1.5 rounded-lg hover:bg-white disabled:opacity-30"><CaretRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Form Dialog */}
      <LeadFormDialog open={showForm} onClose={() => setShowForm(false)} statuses={statuses} campaigns={campaigns} sellers={sellers} isAdmin={isAdmin} onSuccess={() => { setShowForm(false); loadLeads(); }} />

      {/* Settings Dialog */}
      {showSettings && <SettingsDialog onClose={() => { setShowSettings(false); loadMeta(); }} campaigns={campaigns} statuses={statuses} />}
    </div>
  );
}

function LeadFormDialog({ open, onClose, statuses, campaigns, sellers, isAdmin, onSuccess }) {
  const { token } = useAuth();
  const [form, setForm] = useState({ company_name: "", contact_name: "", phone: "", whatsapp: "", email: "", campaign_type: "redes_sociales", campaign_name: "", status: "nuevo", notes: "", assigned_to_id: "", assigned_to_name: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSave = async () => {
    if (!form.contact_name) return toast.error("Nombre de contacto requerido");
    setSaving(true);
    try {
      await axios.post(`${API}/marketing/leads`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Lead creado");
      setForm({ company_name: "", contact_name: "", phone: "", whatsapp: "", email: "", campaign_type: "redes_sociales", campaign_name: "", status: "nuevo", notes: "", assigned_to_id: "", assigned_to_name: "" });
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nuevo Lead</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Empresa</Label><Input value={form.company_name} onChange={e => set("company_name", e.target.value)} className="mt-1" data-testid="mkt-form-company" /></div>
            <div><Label>Contacto *</Label><Input value={form.contact_name} onChange={e => set("contact_name", e.target.value)} className="mt-1" data-testid="mkt-form-contact" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Telefono</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} className="mt-1" /></div>
            <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} className="mt-1" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={e => set("email", e.target.value)} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo de Campana</Label>
              <Select value={form.campaign_type} onValueChange={v => set("campaign_type", v)}>
                <SelectTrigger className="mt-1" data-testid="mkt-form-campaign"><SelectValue /></SelectTrigger>
                <SelectContent>{campaigns.map(c => <SelectItem key={c.name} value={c.name}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nombre de Campana</Label><Input value={form.campaign_name} onChange={e => set("campaign_name", e.target.value)} className="mt-1" placeholder="Ej: Black Friday 2026" /></div>
          </div>
          {isAdmin && sellers.length > 0 && (
            <div>
              <Label>Asignar a Asesor</Label>
              <Select value={form.assigned_to_id || "sin_asignar"} onValueChange={v => {
                const s = sellers.find(sl => sl.id === v);
                set("assigned_to_id", v === "sin_asignar" ? "" : v);
                setForm(f => ({ ...f, assigned_to_id: v === "sin_asignar" ? "" : v, assigned_to_name: s?.name || "" }));
              }}>
                <SelectTrigger className="mt-1" data-testid="mkt-form-seller"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin_asignar">Sin asignar</SelectItem>
                  {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Notas</Label><Textarea value={form.notes} onChange={e => set("notes", e.target.value)} className="mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="mkt-form-save">{saving ? "Guardando..." : "Crear Lead"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadDetail({ leadId, statuses, campaigns, sellers, onBack, isAdmin }) {
  const { token, user } = useAuth();
  const [lead, setLead] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    const [l, h] = await Promise.all([
      axios.get(`${API}/marketing/leads/${leadId}`, { headers }),
      axios.get(`${API}/marketing/leads/${leadId}/history`, { headers }),
    ]);
    setLead(l.data); setHistory(h.data);
    setLoading(false);
  }, [leadId, token]);

  useEffect(() => { load(); }, [load]);

  const update = async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/marketing/leads/${leadId}`, data, { headers });
      toast.success("Actualizado");
      load();
    } catch (e) { toast.error("Error"); }
    setSaving(false);
  };

  const addFollowUp = async () => {
    if (!comment.trim()) return;
    await axios.post(`${API}/marketing/leads/${leadId}/follow-up`, { comment }, { headers });
    setComment(""); load();
    toast.success("Seguimiento agregado");
  };

  const deleteLead = async () => {
    if (!window.confirm("Eliminar este lead?")) return;
    await axios.delete(`${API}/marketing/leads/${leadId}`, { headers });
    toast.success("Lead eliminado"); onBack();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  if (!lead) return null;

  return (
    <div className="space-y-5" data-testid="mkt-lead-detail">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg"><CaretLeft size={20} className="text-slate-500" /></button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-900">{lead.company_name || lead.contact_name}</h2>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusBadge status={lead.status} statuses={statuses} />
            <CampaignBadge type={lead.campaign_type} campaigns={campaigns} />
            {lead.approved_by_admin && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Aprobado</Badge>}
            {lead.comercial_contact_id && <Badge className="bg-blue-100 text-blue-700 text-[10px]">En Comercial</Badge>}
          </div>
        </div>
        {isAdmin && <Button variant="destructive" size="sm" onClick={deleteLead}><Trash size={14} className="mr-1" /> Eliminar</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Info + Actions */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Informacion del Lead</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center gap-2"><Buildings size={14} className="text-slate-400" /> <span className="text-slate-500">Empresa:</span> <span className="font-medium text-slate-700">{lead.company_name || "—"}</span></div>
              <div className="flex items-center gap-2"><User size={14} className="text-slate-400" /> <span className="text-slate-500">Contacto:</span> <span className="font-medium text-slate-700">{lead.contact_name}</span></div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> <span className="text-slate-500">Telefono:</span> <span className="font-medium text-slate-700">{lead.phone || "—"}</span></div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> <span className="text-slate-500">WhatsApp:</span> <span className="font-medium text-slate-700">{lead.whatsapp || "—"}</span></div>
              <div className="flex items-center gap-2"><Envelope size={14} className="text-slate-400" /> <span className="text-slate-500">Email:</span> <span className="font-medium text-slate-700">{lead.email || "—"}</span></div>
              <div className="flex items-center gap-2"><Megaphone size={14} className="text-slate-400" /> <span className="text-slate-500">Campana:</span> <span className="font-medium text-slate-700">{lead.campaign_name || "—"}</span></div>
              <div className="flex items-center gap-2"><User size={14} className="text-slate-400" /> <span className="text-slate-500">Asesor:</span> <span className="font-medium text-slate-700">{lead.assigned_to_name || "Sin asignar"}</span></div>
              <div className="flex items-center gap-2"><Tag size={14} className="text-slate-400" /> <span className="text-slate-500">Creado:</span> <span className="font-medium text-slate-700">{lead.created_at?.slice(0, 10)}</span></div>
            </div>
            {lead.notes && <p className="mt-3 text-xs text-slate-600 bg-slate-50 p-3 rounded-lg">{lead.notes}</p>}
          </div>

          {/* Admin controls */}
          <div className="bg-white rounded-xl border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Acciones</h3>
            {lead.comercial_contact_id && (
              <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2" data-testid="mkt-comercial-badge">
                <Check size={16} className="text-emerald-600" />
                <span className="text-xs font-medium text-emerald-700">Este lead fue enviado al modulo Comercial con el asesor: <strong>{lead.assigned_to_name}</strong></span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cambiar Estatus</Label>
                <Select value={lead.status} onValueChange={v => update({ status: v })} disabled={saving}>
                  <SelectTrigger className="mt-1 text-xs" data-testid="mkt-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map(s => <SelectItem key={s.name} value={s.name}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div>
                  <Label className="text-xs">Asignar Asesor</Label>
                  <Select value={lead.assigned_to_id || "sin_asignar"} onValueChange={v => {
                    const s = sellers.find(sl => sl.id === v);
                    update({ assigned_to_id: v === "sin_asignar" ? null : v, assigned_to_name: s?.name || "" });
                  }} disabled={saving}>
                    <SelectTrigger className="mt-1 text-xs" data-testid="mkt-detail-seller"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin_asignar">Sin asignar</SelectItem>
                      {sellers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {isAdmin && (
              <div className="mt-3">
                <Button variant={lead.approved_by_admin ? "outline" : "default"} size="sm" onClick={() => update({ approved_by_admin: !lead.approved_by_admin })} disabled={saving} data-testid="mkt-detail-approve">
                  <Check size={14} className="mr-1" /> {lead.approved_by_admin ? "Revocar Aprobacion" : "Aprobar para Seguimiento"}
                </Button>
              </div>
            )}
          </div>

          {/* Follow-up */}
          <div className="bg-white rounded-xl border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Seguimiento</h3>
            <div className="flex gap-2 mb-4">
              <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Agregar nota de seguimiento..." className="text-xs" rows={2} data-testid="mkt-followup-input" />
              <Button onClick={addFollowUp} size="sm" className="self-end" data-testid="mkt-followup-btn"><ChatCircleDots size={14} className="mr-1" /> Enviar</Button>
            </div>
          </div>
        </div>

        {/* Right: History */}
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Historial</h3>
          {history.length === 0 ? <p className="text-xs text-slate-400 text-center py-8">Sin historial</p> : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {history.map(h => (
                <div key={h.id} className="border-l-2 border-blue-200 pl-3 pb-2">
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <span className="font-medium text-slate-600">{h.user_name}</span>
                    <span>{h.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="text-xs text-slate-700 mt-0.5">{h.details}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsDialog({ onClose, campaigns, statuses }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [newCampaign, setNewCampaign] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const addCampaign = async () => {
    if (!newCampaign.trim()) return;
    await axios.post(`${API}/marketing/campaign-types`, { name: newCampaign, label: newCampaign, color: "#6B7280" }, { headers });
    toast.success("Campana agregada"); setNewCampaign(""); onClose();
  };
  const addStatus = async () => {
    if (!newStatus.trim()) return;
    await axios.post(`${API}/marketing/status-types`, { name: newStatus, label: newStatus, color: "#6B7280" }, { headers });
    toast.success("Estatus agregado"); setNewStatus(""); onClose();
  };
  const delCampaign = async (id) => { await axios.delete(`${API}/marketing/campaign-types/${id}`, { headers }); toast.success("Eliminado"); onClose(); };
  const delStatus = async (id) => { await axios.delete(`${API}/marketing/status-types/${id}`, { headers }); toast.success("Eliminado"); onClose(); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Configuracion de Marketing</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Tipos de Campana</h4>
            <div className="space-y-1 mb-2">
              {campaigns.map(c => (
                <div key={c.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                    <span>{c.label}</span>
                  </div>
                  <button onClick={() => delCampaign(c.id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newCampaign} onChange={e => setNewCampaign(e.target.value)} placeholder="Nueva campana..." className="text-xs h-8" data-testid="mkt-new-campaign" />
              <Button size="sm" onClick={addCampaign} className="h-8"><Plus size={14} /></Button>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Tipos de Estatus</h4>
            <div className="space-y-1 mb-2">
              {statuses.map(s => (
                <div key={s.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                    <span>{s.label}</span>
                  </div>
                  <button onClick={() => delStatus(s.id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newStatus} onChange={e => setNewStatus(e.target.value)} placeholder="Nuevo estatus..." className="text-xs h-8" data-testid="mkt-new-status" />
              <Button size="sm" onClick={addStatus} className="h-8"><Plus size={14} /></Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
